import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EquipmentPaymentType } from '@app/sales/enums/equipment-payment-type';
import { MobilePhoneFormControls } from '@app/sales/modules/mobilephone/components/mobilephone-search/mobilephone-details/mobilephone-details.component';
import { CustomerProductSelectors } from '@app/sales/states/customer-product/customer-product.selectors';
import { ShoppingCartSelectors } from '@app/sales/states/shopping-cart/shopping-cart.selectors';
import { GtinValidator } from '@app/sales/validators/gtin-validator';
import { select, Store } from '@ngxs/store';
import { EquipmentEventService } from '../../../equipment-event.service';
import { Subscription } from 'rxjs';
import { AccessoryProductAddAction } from '@app/sales/states/accessory-product/accessory-product.actions';
import { MatDialogRef } from '@angular/material/dialog';
import { EquipmentDialogComponent } from '../../equipment-dialog.component';
import { VoiceSubscriptionOffering } from '@app/sales/modules/subscription/models/offerings-response.model';
import { AccessoryProductHelperService } from '@app/sales/services/accessory-product-helper.service';
import { SubscriptionBundleOption } from '@app/sales/modules/mobilephone/models/mobilephone-search-item';
import { EquipmentVariant } from '../../../equipment.models';
import { HardwareVariantOfferingDetails } from '@app/sales/modules/accessories/models/hardware-offering-model';
import { ProductType } from '@app/sales/modules/mobilephone/models/mobilephone-search-enums';
import { DuplicateImeiInShoppingCartValidator, ImeiValidator } from '@app/sales/validators/imei-validator';
import { Regex } from '@app/shared/enums/regex';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { IceButtonComponent } from '@app/shared/components/atoms/ice-button/ice-button.component';
import { EquipmentVoiceNumberTypeComponent } from '../equipment-voice-number-type/equipment-voice-number-type.component';
import { AccessoryPaymentDetailsComponent } from './accessory-payment-details/accessory-payment-details.component';
import { EquipmentIdentificationComponent } from '../equipment-identification/equipment-identification.component';

export interface AccessoryForm {
  gtin: FormControl<string>;
  imei: FormControl<string>;
  productName: FormControl<string>;
  paymentMethod: FormControl<EquipmentPaymentType>;
  isSubscriptionUpgrade: FormControl<string>;
  newOfferingId: FormControl<string>;
  subscriptionIdToUpgrade: FormControl<string>;
}

@Component({
  selector: 'app-manual-registration-accessories-form',
  templateUrl: './manual-registration-accessories-form.component.html',
  styleUrl: './manual-registration-accessories-form.component.scss',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    EquipmentIdentificationComponent,
    AccessoryPaymentDetailsComponent,
    EquipmentVoiceNumberTypeComponent,
    IceButtonComponent,
  ],
})
export class ManualRegistrationAccessoriesFormComponent implements OnInit, OnDestroy {
  private equipmentEventService = inject(EquipmentEventService);
  private store = inject(Store);
  private dialogRef = inject<MatDialogRef<EquipmentDialogComponent>>(MatDialogRef);

  EquipmentVariant = EquipmentVariant;

  existingVoiceProducts = select(CustomerProductSelectors.getExistingVoiceProducts);
  unchangedVoiceSubscriptions = select(CustomerProductSelectors.getUnchangedExistingVoiceSubscriptions);
  isSubscriptionLimitReached = select(CustomerProductSelectors.isSubscriptionLimitReached);
  allOfferings = select(CustomerProductSelectors.getOfferingsIncludingCustomerBTL);
  imeisInShoppingCart = select(ShoppingCartSelectors.imeisInShoppingCart);
  canUpdateImei = select(DealerStateSelectors.canUpdateImei);

  accessoryForm: FormGroup;
  hardwareDetails: HardwareVariantOfferingDetails;

  isFetchingProductData = false;
  subscription$: Subscription;

  get paymentMethodControl(): FormControl {
    return this.accessoryForm?.controls.paymentMethod as FormControl;
  }

  ngOnInit(): void {
    this.accessoryForm = this.buildForm();

    this.subscription$ = this.equipmentEventService.hardwareDetails.subscribe(hardware => {
      this.hardwareDetails = hardware;

      if (hardware?.productType === ProductType.Cellphone) {
        this.accessoryForm.controls.gtin.setErrors({ invalidCellPhoneProductType: true });
      }
    });
  }

  submit(): void {
    if (this.accessoryForm.invalid) return;

    const { createSubscriptionDataForHardwareOffering, createEquipment, createHardwareProduct } =
      AccessoryProductHelperService;

    const equipment = createEquipment(this.hardwareDetails, this.accessoryForm);

    const newOfferingIdValue = this.accessoryForm.controls.newOfferingId.value;
    const hasMatchingBundle = !!this.hardwareDetails?.paymentInformation?.subscriptionBundleOptions?.find(
      bundle => bundle.subscriptionOfferingId === newOfferingIdValue
    );
    const subscriptionOptions: SubscriptionBundleOption[] | VoiceSubscriptionOffering[] = hasMatchingBundle
      ? this.hardwareDetails.paymentInformation?.subscriptionBundleOptions
      : this.allOfferings();
    const { subscription, availableSubscriptions } = createSubscriptionDataForHardwareOffering(
      newOfferingIdValue,
      subscriptionOptions,
      equipment
    );

    if (!!subscription) {
      const hardwareProductToAdd = createHardwareProduct(
        this.hardwareDetails,
        subscription,
        availableSubscriptions,
        this.accessoryForm
      );

      this.store.dispatch(new AccessoryProductAddAction(hardwareProductToAdd));
    }

    const navigateToSubscriptionPage = true;
    this.dialogRef.close(navigateToSubscriptionPage);
  }

  private buildForm(): FormGroup {
    let form = new FormGroup<AccessoryForm>({
      gtin: new FormControl('', [Validators.required, GtinValidator()]),
      imei: new FormControl({ value: '', disabled: true }, [
        Validators.required,
        Validators.minLength(15),
        Validators.maxLength(16),
        Validators.pattern(Regex.IMEI),
        ImeiValidator(),
      ]),
      productName: new FormControl({ value: '', disabled: true }, Validators.required),
      paymentMethod: new FormControl(EquipmentPaymentType.PayNowWithSubscription),
      isSubscriptionUpgrade: new FormControl('', [Validators.required]),
      newOfferingId: new FormControl(null, [Validators.required]),
      subscriptionIdToUpgrade: new FormControl('', [Validators.required]),
    });

    if (this.canUpdateImei())
      form.controls.imei.addValidators([DuplicateImeiInShoppingCartValidator(this.imeisInShoppingCart())]);

    // Form controls required for equipment voice number type
    form.addControl(MobilePhoneFormControls.NewOfferingId, new FormControl(null, [Validators.required]));
    form.addControl(MobilePhoneFormControls.IsSubscriptionUpgrade, new FormControl('', [Validators.required]));
    form.addControl(MobilePhoneFormControls.SubscriptionIdToUpgrade, new FormControl('', [Validators.required]));

    return form;
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
